import Modal from 'react-modal'; // It can be any modal
import { useState } from 'react';
import { MODALS, MODAL_TYPES } from 'src/@types/navigation';
import { SCREENS } from 'src/constants/strings';
import theme, { Box, Text } from 'src/theme';
import { useQuery, useMutation } from 'react-query';
import { ROUTES } from 'src/api/Routes';
import { fetchNotifications } from 'src/screens/MainContent/UserSettings/NotificationsTab/api';
import { Grid } from '@mui/material';
import { sendNotification } from '../screens/MainContent/AddCustomer/api';
import { useMediaQuery } from 'react-responsive';
import { mobileMaxWidth } from 'src/utils/responsiveSize';
import useWhatsAppStatus from 'src/hooks/useWhatsAppStatus';

const extractTextFromHtml = (html) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

type SendAll = {
  notificationId: number;
  notificationType: 'email' | 'whatsapp';
};

export default function SendAlertModal({
  isOpen,
  onClose,
  contentStyle,
  customersIds,
  phoneNumber,
}: MODALS[MODAL_TYPES.SEND_ALERT]): JSX.Element {
  const isMobile = useMediaQuery({ query: mobileMaxWidth });
  const [error, setError] = useState('');

  const { data } = useQuery([ROUTES.GET.GET_NOTIFICATIONS], fetchNotifications);
  // When the business has a linked phone the server sends; otherwise the
  // button falls back to opening a wa.me link, which needs a phone number.
  const { isConnected } = useWhatsAppStatus(isOpen);

  const sendAll = ({ notificationId, notificationType }: SendAll) =>
    Promise.all(
      customersIds.map((customerId) =>
        sendNotification({ customerId, notificationId, notificationType }),
      ),
    );
  const sendMutation = useMutation(sendAll, {
    onSuccess: () => {
      onClose();
    },
    onError: () => {
      setError(SCREENS.SEND_NOTIFICATION_FAILED);
    },
  });
  if (!data) {
    return <Box></Box>;
  }

  const buttonStyle = {
    backgroundColor: theme.colors.blue6ea2ff,
    borderColor: theme.colors.blue6ea2ff,
    color: theme.colors.white,
    borderStyle: 'solid',
    width: isMobile ? 50 : 110,
    height: 36,
    borderRadius: 10,
    justifyContent: 'center',
    cursor: sendMutation.isLoading ? 'default' : 'pointer',
    alignSelf: 'center',
    fontSize: isMobile ? 10 : 12,
    opacity: sendMutation.isLoading ? 0.6 : 1,
  };

  const openWhatsAppLink = (text: string) => {
    const url = `https://api.whatsapp.com/send?phone=972${phoneNumber.replaceAll(
      '-',
      '',
    )}&text=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Modal
      ariaHideApp={false}
      style={{
        overlay: {
          zIndex: 1000,
          backgroundColor: '#3737372E',
        },
        content: {
          ...contentStyle,
          padding: 0,
        },
      }}
      isOpen={isOpen}
      onRequestClose={onClose}>
      <Box textAlign={'center'}>
        <Grid
          container
          sx={{
            borderRadius: 2,
            borderColor: theme.colors.greye9ecef,
            borderStyle: 'solid',
          }}>
          <Grid
            item
            md={12}
            sx={{
              borderBlockColor: theme.colors.greye9ecef,
              borderBlockStyle: 'solid',
              backgroundColor: theme.colors.greyf8f9fa,
              flex: 1,
              py: 2,
              px: 3,
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'space-between',
            }}>
            <Text fontSize={12} color="grey686868">
              {SCREENS.CREATE_NOTIFICATION_TABLE_TITLE}
            </Text>
          </Grid>

          {data.map((notification) => {
            const whatsAppMessage = extractTextFromHtml(
              notification.whatsappMessage,
            );
            let filesListString = '';
            notification.files.map((file) => {
              filesListString += `\n${file.file}`;
            });

            return (
              <Grid
                item
                md={12}
                key={notification.id}
                sx={{
                  flex: 1,
                  borderBottomColor: theme.colors.greye9ecef,
                  borderBottomStyle: 'solid',
                  py: 2,
                  px: 3,
                  alignItems: 'center',
                  display: 'flex',
                }}>
                <Box
                  flex={1}
                  justifyContent="space-between"
                  display="flex"
                  alignItems="center">
                  <Box flex={6}>
                    <Text fontSize={isMobile ? 12 : 16} textAlign="start">
                      {notification.notificationName}
                    </Text>
                  </Box>
                  <Box display="flex" flex={1}>
                    <Box pl={2}>
                      <button
                        disabled={sendMutation.isLoading}
                        onClick={() => {
                          sendMutation.mutate({
                            notificationId: notification.id,
                            notificationType: 'email',
                          });
                        }}
                        style={buttonStyle}>
                        {SCREENS.SEND_NOTIFICATION_EMAIL}
                      </button>
                    </Box>
                    {(isConnected || phoneNumber) && (
                      <Box>
                        <button
                          disabled={sendMutation.isLoading}
                          onClick={() => {
                            if (isConnected) {
                              sendMutation.mutate({
                                notificationId: notification.id,
                                notificationType: 'whatsapp',
                              });
                            } else {
                              openWhatsAppLink(
                                whatsAppMessage + filesListString,
                              );
                            }
                          }}
                          style={buttonStyle}>
                          {SCREENS.SEND_NOTIFICATION_WHATSAPP}
                        </button>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
        {error && (
          <Text fontSize={12} color="redError" py={2}>
            {error}
          </Text>
        )}
      </Box>
    </Modal>
  );
}
